import { useEffect } from 'react'
import { isDocuSignReturnMessage } from '../../../../docusign/isDocuSignReturnMessage'
import { isTrustedDocuSignReturnOrigin } from '../../../../docusign/isTrustedDocuSignReturnOrigin'
import { SELF_ENROLLMENT_STEPS } from '../../utils/steps'
import { getDocuSignReturnMessage } from '../../utils/utils'

type UseDocusignFinalizationPollingArgs = {
  currentStep: number
  enrollmentSessionToken: string | null
  isDocusignModalOpen: boolean
  isFinalizingDocusign: boolean
  isSignatureSigned: boolean
  refreshSignatureStatus: () => Promise<boolean>
  setIsCheckingSignature: (value: boolean) => void
}

export function useDocusignFinalizationPolling({
  currentStep,
  enrollmentSessionToken,
  isDocusignModalOpen,
  isFinalizingDocusign,
  isSignatureSigned,
  refreshSignatureStatus,
  setIsCheckingSignature
}: UseDocusignFinalizationPollingArgs) {
  useEffect(() => {
    if (
      currentStep >= SELF_ENROLLMENT_STEPS.SIGNED_COMPLETE ||
      !enrollmentSessionToken ||
      !isDocusignModalOpen ||
      !isFinalizingDocusign ||
      isSignatureSigned
    ) {
      return
    }

    let timeoutId: number | undefined
    let cancelled = false

    const poll = async () => {
      try {
        await refreshSignatureStatus()
      } finally {
        if (cancelled) {
          return
        }

        timeoutId = window.setTimeout(() => {
          void poll()
        }, 5000)
      }
    }

    void poll()

    return () => {
      cancelled = true
      setIsCheckingSignature(false)
      if (timeoutId) {
        window.clearTimeout(timeoutId)
      }
    }
  }, [
    currentStep,
    enrollmentSessionToken,
    isDocusignModalOpen,
    isFinalizingDocusign,
    isSignatureSigned,
    refreshSignatureStatus,
    setIsCheckingSignature
  ])
}

type UseDocusignWindowFocusEffectsArgs = {
  isDocusignModalOpen: boolean
  isSignatureSigned: boolean
  hasLoadedDocusignIframeRef: { current: boolean }
  hasBlurredDuringDocusignRef: { current: boolean }
  setIsFinalizingDocusign: (value: boolean) => void
  setIsDocusignIframeLoading: (value: boolean) => void
  refreshSignatureStatus: () => Promise<boolean>
}

export function useDocusignWindowFocusEffects({
  isDocusignModalOpen,
  isSignatureSigned,
  hasLoadedDocusignIframeRef,
  hasBlurredDuringDocusignRef,
  setIsFinalizingDocusign,
  setIsDocusignIframeLoading,
  refreshSignatureStatus
}: UseDocusignWindowFocusEffectsArgs) {
  useEffect(() => {
    if (!isDocusignModalOpen || !hasLoadedDocusignIframeRef.current || isSignatureSigned) {
      return
    }

    const handleWindowBlur = () => {
      hasBlurredDuringDocusignRef.current = true
    }

    const handleWindowFocus = () => {
      if (!hasBlurredDuringDocusignRef.current || isSignatureSigned) {
        return
      }

      hasBlurredDuringDocusignRef.current = false
      setIsFinalizingDocusign(true)
      setIsDocusignIframeLoading(true)
      void refreshSignatureStatus()
    }

    window.addEventListener('blur', handleWindowBlur)
    window.addEventListener('focus', handleWindowFocus)

    return () => {
      window.removeEventListener('blur', handleWindowBlur)
      window.removeEventListener('focus', handleWindowFocus)
    }
  }, [
    hasBlurredDuringDocusignRef,
    hasLoadedDocusignIframeRef,
    isDocusignModalOpen,
    isSignatureSigned,
    refreshSignatureStatus,
    setIsDocusignIframeLoading,
    setIsFinalizingDocusign
  ])
}

type UseDocusignReturnListenerArgs = {
  isDocusignModalOpen: boolean
  isSignatureSigned: boolean
  closeDocusignModal: () => void
  refreshSignatureStatus: () => Promise<boolean>
  setIsFinalizingDocusign: (value: boolean) => void
  setIsDocusignIframeLoading: (value: boolean) => void
  setStepSubmissionError: (value: string | null) => void
}

export function useDocusignReturnListener({
  isDocusignModalOpen,
  isSignatureSigned,
  closeDocusignModal,
  refreshSignatureStatus,
  setIsFinalizingDocusign,
  setIsDocusignIframeLoading,
  setStepSubmissionError
}: UseDocusignReturnListenerArgs) {
  useEffect(() => {
    const handleDocuSignReturn = (message: MessageEvent) => {
      if (!isDocusignModalOpen || isSignatureSigned) {
        return
      }

      if (!isTrustedDocuSignReturnOrigin(message.origin)) {
        return
      }

      const data: unknown = message.data
      if (!isDocuSignReturnMessage(data)) {
        return
      }

      const eventName = data.event.toLowerCase()

      if (eventName === 'signing_complete') {
        setIsFinalizingDocusign(true)
        setIsDocusignIframeLoading(true)
        void refreshSignatureStatus()
        return
      }

      closeDocusignModal()
      setStepSubmissionError(getDocuSignReturnMessage(eventName))
    }

    window.addEventListener('message', handleDocuSignReturn)

    return () => {
      window.removeEventListener('message', handleDocuSignReturn)
    }
  }, [
    closeDocusignModal,
    isDocusignModalOpen,
    isSignatureSigned,
    refreshSignatureStatus,
    setIsDocusignIframeLoading,
    setIsFinalizingDocusign,
    setStepSubmissionError
  ])
}
